import { Component } from '@theme/component';

/**
 * Holds an image's `src`/`srcset` in data attributes until the element nears the viewport.
 *
 * Native `loading="lazy"` looks up to 2500px ahead on slow connections, so images a screen or two
 * below the fold still download during page load and compete with the LCP image.
 */
class EqDeferredImage extends Component {
  #observer = null;
  #mutationObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#arm();
    // A morph re-render keeps this element but strips the loaded `src` back to `data-src`, so re-arm.
    this.#mutationObserver = new MutationObserver(() => {
      if (!this.#observer) this.#arm();
    });
    this.#mutationObserver.observe(this, { subtree: true, childList: true, attributeFilter: ['data-src'] });
  }

  disconnectedCallback() {
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;
    this.#observer?.disconnect();
    this.#observer = null;
    super.disconnectedCallback();
  }

  #arm() {
    const images = this.querySelectorAll('img[data-src]');
    if (!images.length) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#load();
      },
      // A full viewport sideways, so marquee items that animate in from off-screen are already loaded.
      // `rootMargin` stops at a carousel's overflow clip; `scrollMargin` widens the carousel itself.
      { rootMargin: '200px 100%', scrollMargin: '0px 100%' }
    );
    for (const img of images) this.#observer.observe(img);
  }

  #load() {
    this.#observer?.disconnect();
    this.#observer = null;
    for (const img of this.querySelectorAll('img[data-src]')) {
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    }
  }
}

if (!customElements.get('eq-deferred-image')) {
  customElements.define('eq-deferred-image', EqDeferredImage);
}
